import React, { useState, useEffect, useCallback } from 'react';
import { kaizenApi, resolveMediaUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SubmissionFields, countWords, MAX_WORDS } from '../components/SubmissionForm';
import BenefitTag from '../components/BenefitTag';
import { toast } from 'sonner';

const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/assets/aampl-logo.jpg';
export default function KaizenTab({ employeeId, employee, canEdit }) {
  const blank = {
    kaizen_no: '',
    // Reporting month this kaizen counts toward on the dashboard. Explicit
    // rather than derived from the save date, so one typed up in September
    // for an August improvement still lands in August.
    period: new Date().toISOString().slice(0, 7),
    department: employee?.department || '', suggested_by: employee?.name || '', suggested_by_id: employeeId,
    approved_by: '', implemented_by: '', implementation_date: '', subject: '', process: '',
    before_text: '', after_text: '', before_photo: null, after_photo: null,
    before_video: null, after_video: null, benefit_notes: '', total_cost_saving: '',
    // The fields a kaizen now shares with a suggestion, so the two can be
    // reported on as one register. The HR-F-16 fields above are untouched.
    title: '', description: '', benefit_category: '', benefit_amount: '', attachment: null,
  };
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await kaizenApi.list({ employee_id: employeeId }); setRows(r.data || []); }
    catch (e) { toast.error('Failed to load kaizens'); }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [uploading, setUploading] = useState(null);
  // Which Before/After panel a pasted image lands in: whichever was clicked
  // last, else the first one still empty.
  const [pasteInto, setPasteInto] = useState('before');

  // One picker for both kinds. A photo is small enough to inline as base64 the
  // way it always has; a video is uploaded first and stored by reference.
  const pickMedia = async (side, file) => {
    if (!file) return;
    const isVideo = (file.type || '').startsWith('video/');
    if (!isVideo && !(file.type || '').startsWith('image/')) {
      toast.error('Choose a photo or a video'); return;
    }
    if (!isVideo) {
      if (file.size > 3 * 1024 * 1024) { toast.error('Image too large (max 3 MB)'); return; }
      const reader = new FileReader();
      reader.onload = () => set(`${side}_photo`, reader.result);
      reader.readAsDataURL(file);
      return;
    }
    if (file.size > 25 * 1024 * 1024) { toast.error('Video too large (max 25 MB)'); return; }
    setUploading(side);
    try {
      const r = await kaizenApi.uploadMedia(file);
      set(`${side}_video`, r.data.url);
      toast.success('Video uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Video upload failed'); }
    finally { setUploading(null); }
  };

  // Pasting a copied image (Ctrl+V) is the quickest route for a photo already
  // on the clipboard — a screenshot, or something copied out of WhatsApp —
  // with no round trip through the filesystem. Only while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      const item = Array.from(e.clipboardData?.items || [])
        .find((i) => (i.type || '').startsWith('image/'));
      const file = item?.getAsFile();
      if (!file) return;
      e.preventDefault();
      setF((cur) => {
        const side = !cur[`${pasteInto}_photo`] ? pasteInto
          : (!cur.before_photo ? 'before' : (!cur.after_photo ? 'after' : pasteInto));
        pickMedia(side, file);
        return cur;
      });
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, pasteInto]);

  // Before/After improvement panels share their layout entirely. This is called
  // as a plain function rather than rendered as <MediaSide/>: a component
  // defined inside the body is a new type on every render, so React would
  // remount the subtree and the textarea would lose focus on each keystroke.
  const mediaSide = (side, label) => (
    <div key={side} onClick={() => setPasteInto(side)}
         className={pasteInto === side ? 'rounded-lg ring-2 ring-[#BFD8FF] p-1 -m-1' : 'p-1 -m-1'}>
      <label className="text-xs text-[#5A6377]">{label}</label>
      <textarea value={f[`${side}_text`]} onChange={(e) => set(`${side}_text`, e.target.value)} rows={3}
        className="w-full border border-[#E6E9F0] rounded-lg p-2 text-sm" />
      <label className="text-xs text-[#2563EB] cursor-pointer">
        {uploading === side ? 'Uploading…' : 'Photo / Video'}
        <input type="file" accept="image/*,video/*" className="hidden" disabled={uploading === side}
          onChange={(e) => { pickMedia(side, e.target.files?.[0]); e.target.value = ''; }} />
      </label>
      <span className="text-[11px] text-[#98A1B2] ml-2">or paste an image (Ctrl+V)</span>
      {f[`${side}_photo`] && (
        <div className="mt-1">
          {/* A bare max-height let a wide photo run past the panel and gave
              every row a different height. Fixed frame, image contained. */}
          <div className="w-full h-36 rounded border border-[#E6E9F0] bg-[#F2F4F8] flex items-center justify-center overflow-hidden">
            <img src={resolveMediaUrl(f[`${side}_photo`])} alt={side}
              data-testid={`kaizen-photo-${side}`}
              className="max-w-full max-h-full w-auto h-auto object-contain" />
          </div>
          <button onClick={() => set(`${side}_photo`, null)}
            className="text-[11px] text-[#C2151F] hover:underline mt-1">Remove photo</button>
        </div>
      )}
      {f[`${side}_video`] && (
        <div className="mt-1">
          <video src={resolveMediaUrl(f[`${side}_video`])} controls className="max-h-32 rounded w-full" />
          <button onClick={() => set(`${side}_video`, null)}
            className="text-[11px] text-[#C2151F] hover:underline">Remove video</button>
        </div>
      )}
    </div>
  );
  const openNew = () => { setF(blank); setEditId(null); setOpen(true); };
  const openEdit = (k) => { setF({ ...blank, ...k, suggested_by_id: k.suggested_by_id || employeeId }); setEditId(k.id); setOpen(true); };
  const save = async () => {
    // Same rules as the suggestion form; the server enforces them too, this is
    // just so HR hears about it before the round trip.
    if (!String(f.title || '').trim()) { toast.error('Please enter a short title.'); return; }
    const words = countWords(f.description);
    if (!words) { toast.error('Please describe the improvement.'); return; }
    if (words > MAX_WORDS) { toast.error(`Maximum ${MAX_WORDS} words allowed.`); return; }
    if (!f.benefit_category) { toast.error('Please select a benefit category.'); return; }
    if (f.benefit_category === 'Commercial') {
      const a = String(f.benefit_amount).trim();
      if (!a) { toast.error('Please enter the benefit amount.'); return; }
      if (Number.isNaN(Number(a)) || Number(a) < 0) { toast.error('The benefit amount must be a number.'); return; }
    }
    setBusy(true);
    try {
      // The amount belongs to Commercial and only to Commercial. The form keeps
      // it as a STRING so the input stays controlled, and an empty string is
      // not a number: sending it raw made every non-Commercial kaizen fail on
      // the backend with "unable to parse string as a number", which the user
      // saw as Save doing nothing. Normalised to a number, or null.
      const payload = {
        ...f,
        benefit_amount: f.benefit_category === 'Commercial'
          && String(f.benefit_amount).trim() !== ''
          ? Number(f.benefit_amount)
          : null,
      };
      if (editId) await kaizenApi.update(editId, payload); else await kaizenApi.create(payload);
      toast.success('Kaizen saved'); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const remove = async (k) => { if (!window.confirm('Delete this kaizen?')) return; try { await kaizenApi.remove(k.id); load(); } catch (e) { toast.error('Delete failed'); } };
  const print = async (k) => {
    try { const r = await kaizenApi.print(k.id, { logo_url: LOGO_URL }); const w = window.open('', '_blank'); if (w) { w.document.write(r.data.html); w.document.close(); } }
    catch (e) { toast.error('Could not generate sheet'); }
  };

  return (
    <div className="bg-white border border-[#E6E9F0] rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-xl font-bold tracking-[-0.02em] text-[#172554]">Kaizen</h2>
          <p className="text-[11px] text-[#98A1B2]">HR-F-16 format</p>
        </div>
        {canEdit && <Button onClick={openNew} className="bg-[#2563EB] text-white hover:bg-[#C2151F]">+ New Kaizen</Button>}
      </div>

      {rows.length === 0 ? <p className="text-[#98A1B2] py-8 text-center">No kaizens yet for this employee.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
              <tr><th className="py-2 px-3 text-left">Kaizen No.</th><th className="py-2 px-3 text-left">Subject</th><th className="py-2 px-3 text-left">Date</th><th className="py-2 px-3 text-left">Saving/Yr</th><th className="py-2 px-3"></th></tr>
            </thead>
            <tbody>
              {rows.map((k) => (
                <tr key={k.id} className="border-b border-[#F2F4F8]">
                  <td className="py-2 px-3 text-[#11182B] font-medium">{k.kaizen_no || '—'}</td>
                  <td className="py-2 px-3 text-[#5A6377]">
                    {k.title || k.subject || '—'}
                    <BenefitTag row={k} />
                  </td>
                  <td className="py-2 px-3 text-[#5A6377]">{k.implementation_date || '—'}</td>
                  <td className="py-2 px-3 text-[#5A6377]">{k.total_cost_saving || '—'}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button onClick={() => print(k)} className="text-xs text-[#2563EB] mr-3 hover:underline">Print</button>
                    {canEdit && <button onClick={() => openEdit(k)} className="text-xs text-[#5A6377] mr-3 hover:underline">Edit</button>}
                    {canEdit && <button onClick={() => remove(k)} className="text-xs text-[#C2151F] hover:underline">Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Edit' : 'New'} Kaizen (HR-F-16)</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs text-[#5A6377]">Kaizen No.</label><Input value={f.kaizen_no} onChange={(e) => set('kaizen_no', e.target.value)} /></div>
            <div>
              <label className="text-xs text-[#5A6377]">Month</label>
              <Input data-testid="kaizen-period" type="month" value={f.period || ''}
                     onChange={(e) => set('period', e.target.value)} />
            </div>
            <div><label className="text-xs text-[#5A6377]">Department</label><Input value={f.department} onChange={(e) => set('department', e.target.value)} /></div>
            <div><label className="text-xs text-[#5A6377]">Implementation Date</label><Input type="date" value={f.implementation_date} onChange={(e) => set('implementation_date', e.target.value)} /></div>
            <div><label className="text-xs text-[#5A6377]">Suggested By</label><Input value={f.suggested_by} onChange={(e) => set('suggested_by', e.target.value)} /></div>
            <div><label className="text-xs text-[#5A6377]">Approved By</label><Input value={f.approved_by} onChange={(e) => set('approved_by', e.target.value)} /></div>
            <div><label className="text-xs text-[#5A6377]">Implemented By</label><Input value={f.implemented_by} onChange={(e) => set('implemented_by', e.target.value)} /></div>
          </div>
          {/* Title, fifty words and a benefit — the same few fields a
              suggestion carries, so a kaizen can be reported on beside one.
              Kept at the top because they are what HR actually reads back. */}
          <div className="mt-3 rounded-lg border border-[#DBE6FB] bg-[#F7FAFF] p-3">
            <SubmissionFields value={f} onChange={set} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><label className="text-xs text-[#5A6377]">Subject <span className="text-[#98A1B2]">(defaults to the title)</span></label><Input value={f.subject} onChange={(e) => set('subject', e.target.value)} /></div>
            <div><label className="text-xs text-[#5A6377]">Process</label><Input value={f.process} onChange={(e) => set('process', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {mediaSide('before', 'Before improvement')}
            {mediaSide('after', 'After improvement')}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><label className="text-xs text-[#5A6377]">Benefit (notes)</label><Input value={f.benefit_notes} onChange={(e) => set('benefit_notes', e.target.value)} /></div>
            <div><label className="text-xs text-[#5A6377]">Total Cost Saving / Year</label><Input value={f.total_cost_saving} onChange={(e) => set('total_cost_saving', e.target.value)} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#2563EB] text-white hover:bg-[#C2151F]">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
